import { useEffect, useState } from 'react'

import { Link, useLocation } from 'react-router'
import { ThemeToggle } from '../components/common/ThemeToggle'
import { SentryShortcut } from '../components/common/SentryShortcut'
import UserDropdown from '../components/header/UserDropdown'
import { PageTitleService } from '../hooks/usePageTitle'
import { useSidebar } from '../context/sidebar'
import { Icon } from '@/design-system/atoms/Icon'

const AppHeader: React.FC = () => {
  const [pageTitle, setPageTitle] = useState('Dashboard')
  const [breadcrumb, setBreadcrumb] = useState<string[]>([])
  const [isApplicationMenuOpen, setApplicationMenuOpen] = useState(false)
  const location = useLocation()

  const { toggleSidebar, toggleMobileSidebar } = useSidebar()

  // Check if we're on a chat route
  const isChatRoute = location.pathname.includes('/chat')

  const toggleApplicationMenu = () => {
    setApplicationMenuOpen(!isApplicationMenuOpen)
  }

  const handleToggle = () => {
    if (window.innerWidth >= 1024) {
      toggleSidebar()
    } else {
      toggleMobileSidebar()
    }
  }

  // Subscribe to page title changes
  useEffect(() => {
    const unsubscribe = PageTitleService.subscribe((title, breadcrumbs) => {
      setPageTitle(title)
      setBreadcrumb(breadcrumbs)
    })

    return unsubscribe
  }, [])

  return (
    <header className="sticky top-0 flex flex-col w-full bg-white border-gray-200 z-30 dark:border-gray-800 dark:bg-gray-900 lg:border-b">
      <div className="flex flex-col items-center justify-between grow lg:flex-row lg:px-6">
        <div className="flex items-center justify-between w-full gap-2 px-3 py-3 border-b border-gray-200 dark:border-gray-800 sm:gap-4 lg:justify-normal lg:border-b-0 lg:px-0 lg:py-4">
          <button
            className="items-center justify-center w-10 h-10 text-gray-500 border-gray-200 rounded-lg z-99999 dark:border-gray-800 lg:flex dark:text-gray-400 lg:h-11 lg:w-11 lg:border hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            onClick={handleToggle}
            aria-label="Toggle Sidebar"
          >
            <Icon name="list" size={20} />
          </button>

          <Link to="/" className="lg:hidden flex items-center gap-2">
            <img
              className="h-10 w-auto object-contain dark:brightness-110"
              src="/images/logo/logo-icon.png"
              alt="MDi Optimus"
            />
            <img
              className="h-8 w-auto object-contain dark:invert"
              src="/images/logo/mdi-optimus-text.png"
              alt="MDi Optimus"
            />
          </Link>

          <button
            onClick={toggleApplicationMenu}
            className="flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 lg:hidden"
          >
            <Icon name="ellipsis" size={20} />
          </button>

          {/* Page title and breadcrumb */}
          <div className="hidden lg:block">
            <div className="flex items-center gap-2">
              {breadcrumb.length > 0 && (
                <>
                  {breadcrumb.map((item, index) => (
                    <span
                      key={index}
                      className="text-sm text-gray-500 dark:text-gray-400"
                    >
                      {item}
                      {index < breadcrumb.length - 1 && (
                        <span className="mx-2">/</span>
                      )}
                    </span>
                  ))}
                  <span className="mx-2 text-gray-500 dark:text-gray-400">
                    /
                  </span>
                </>
              )}
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {pageTitle}
              </h1>
            </div>
          </div>
        </div>

        <div
          className={`${
            isApplicationMenuOpen ? 'flex' : 'hidden'
          } items-center justify-between w-full gap-4 px-5 py-4 lg:flex shadow-theme-md lg:justify-end lg:px-0 lg:shadow-none`}
        >
          <div className="flex items-center gap-2 2xsm:gap-3">
            <SentryShortcut />
            <ThemeToggle variant="default" />
          </div>
          <UserDropdown />
        </div>
      </div>

      {/* Mobile title bar - discrete bar below main header - hidden for chat routes */}
      {!isChatRoute && (
        <div className="lg:hidden w-full border-b border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 px-4 py-3">
          <h1 className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
            {breadcrumb.length > 0
              ? `${breadcrumb[breadcrumb.length - 1]}: ${pageTitle}`
              : pageTitle}
          </h1>
        </div>
      )}
    </header>
  )
}

export default AppHeader
